/**
 * O que cada perfil e papel vê e faz na tela — Optimale Grid (frontend).
 *
 * Criado em: 16/10/2026 · Última modificação: 26/10/2026 · Versão: 2.11.0
 *
 * US-1801 (ADR-0014): a regra de "quem pode o quê" mora só no backend, na matriz central (`backend/permissoes.py`).
 * O login e `/auth/eu` mandam as `capacidades` do usuário; aqui a tela só mostra ou esconde — quem decide de fato
 * continua sendo o backend, que confere cada pedido e a operadora.
 */
import type { Usuario } from "./api";
import { marcaAtual } from "./marca";

const tem = (u: Usuario, capacidade: string) => (u.capacidades ?? []).includes(capacidade);

export const pode = {
  verEmpresa: (u: Usuario, empresa: string) => u.empresas.includes("*") || u.empresas.includes(empresa),
  manobrar: (u: Usuario) => tem(u, "manobrar"),
  reconhecerAlarme: (u: Usuario) => tem(u, "reconhecer_alarme"),
  editarCadastro: (u: Usuario) => tem(u, "editar_ativos"),
  cadastrarEmCampo: (u: Usuario) => tem(u, "cadastrar_em_campo"),
  // leitura do cadastro para todos (o backend já permite ler a operadora visível)
  verCadastro: (_u: Usuario) => true,
  editarRegras: (u: Usuario) => tem(u, "editar_regras"),
  revogarDispositivo: (u: Usuario) => tem(u, "revogar_dispositivo"),
  configurarVideowall: (u: Usuario) => tem(u, "configurar_videowall"),
  verPaineis: (u: Usuario) => tem(u, "ver_paineis"),
  aprovarPaineis: (u: Usuario) => tem(u, "aprovar_paineis"),
  verTreinamento: (u: Usuario) => tem(u, "ver_treinamento"),
  simularNoServidor: (u: Usuario) => tem(u, "simular_rede"),
  gerenciarUsuarios: (u: Usuario) => tem(u, "gerenciar_usuarios"),
  verUsuarios: (u: Usuario) => tem(u, "ver_usuarios"),
  verEstrutura: (u: Usuario) => tem(u, "ver_estrutura"),
  editarEstrutura: (u: Usuario) => tem(u, "editar_estrutura"),
  verMarca: (u: Usuario) => tem(u, "ver_marca"),  // US-4503
  editarMarca: (u: Usuario) => tem(u, "editar_marca"),  // D-4502: administrador da plataforma
  editarMunicipios: (u: Usuario) => tem(u, "editar_municipios"),
  autorizarManobraAutomatica: (u: Usuario) => tem(u, "autorizar_manobra_automatica"),
  gerenciarCanais: (u: Usuario) => tem(u, "gerenciar_canais"),
  gerenciarCertificados: (u: Usuario) => tem(u, "gerenciar_certificados"),
  executarImplantacao: (u: Usuario) => tem(u, "executar_implantacao"),
  // US-3108: apagar alarme é marcar; histórico completo com os apagados
  apagarAlarme: (u: Usuario) => tem(u, "apagar_alarme"),
  verHistoricoCompleto: (u: Usuario) => tem(u, "ver_historico_completo"),
  // US-3107: auditoria do agente de IA
  verAuditoriaAgente: (u: Usuario) => tem(u, "ver_auditoria_agente"),
  verObservabilidadeLocal: (u: Usuario) => tem(u, "ver_observabilidade_local"), // US-3803
  // US-3010: ajuste de qualidade de um sensor
  ajustarQualidadeSensores: (u: Usuario) => tem(u, "ajustar_qualidade_sensores"),
  // US-3002: calendário de feriados
  editarFeriados: (u: Usuario) => tem(u, "editar_feriados"),
  importarFeriadosNacionais: (u: Usuario) => tem(u, "importar_feriados_nacionais"),
  // US-3701: referências oficiais por arquivo (SNIS/SINISA, ANA)
  importarReferencias: (u: Usuario) => tem(u, "importar_referencias"),
};

export function nomePapeis(u: Usuario): string {
  const nomes: Record<string, string> = { operador: "Operador", supervisor: "Supervisor", coordenador: "Coordenador",
    campo: "Campo", engenharia: "Engenharia", atendimento: "Atendimento", administrador: "Administrador" };
  const papeis = u.papeis.map((p) => nomes[p] ?? p).join(", ");
  return u.perfil === "cliente" ? papeis : `${u.perfil === "suporte" ? "Suporte" : "Integrador"} ${marcaAtual().empresa}${papeis ? ` · ${papeis}` : ""}`;
}
